import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardAction, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip"
import AppLayout from "@/layouts/AppLayout"
import { Head, router, usePage } from "@inertiajs/react"
import {
  IconAlertTriangle,
  IconArrowRight,
  IconBabyCarriage,
  IconBeach,
  IconBook,
  IconCalendarOff,
  IconCheck,
  IconClock,
  IconPlus,
  IconShieldLock,
  IconUsers,
} from "@tabler/icons-react"
import { useEffect, useState } from "react"

interface Balance {
  balance_type: string
  total_remaining: number
  total_available: number
  entries: {
    year: number
    remaining_days: number
    days_available: number
  }[]
}

interface TimeOffRequest {
  id: number
  start_date: string
  end_date: string
  request_type: string
  status: string
  days_requested: number
  user_name?: string
  user_avatar_url?: string
}

interface DashboardProps {
  [key: string]: unknown
  balances: Balance[]
  recent_requests: TimeOffRequest[]
  approved_requests_count: number
  pending_requests_count: number
  all_pending_requests?: TimeOffRequest[]
  total_pending_admin?: number
  total_users?: number
  manager_code?: {
    code: string
    seconds_remaining: number
  }
  auth: {
    user: { name: string; role: string }
    is_admin: boolean
  }
}

const balanceConfig: Record<
  string,
  { icon: typeof IconBeach; color: string; bg: string; track: string; label: string }
> = {
  holiday: {
    icon: IconBeach,
    color: "text-emerald-600 dark:text-emerald-400",
    bg: "bg-emerald-500/15 dark:bg-emerald-500/20",
    track: "bg-emerald-500",
    label: "Holiday",
  },
  study: {
    icon: IconBook,
    color: "text-blue-600 dark:text-blue-400",
    bg: "bg-blue-500/15 dark:bg-blue-500/20",
    track: "bg-blue-500",
    label: "Study",
  },
  parental: {
    icon: IconBabyCarriage,
    color: "text-violet-600 dark:text-violet-400",
    bg: "bg-violet-500/15 dark:bg-violet-500/20",
    track: "bg-violet-500",
    label: "Parental",
  },
  unpaid: {
    icon: IconCalendarOff,
    color: "text-amber-600 dark:text-amber-400",
    bg: "bg-amber-500/15 dark:bg-amber-500/20",
    track: "bg-amber-500",
    label: "Unpaid",
  },
}

const statusStyles: Record<string, { badge: string; dot: string }> = {
  approved: {
    badge: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
    dot: "bg-emerald-500",
  },
  pending: {
    badge: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
    dot: "bg-amber-500",
  },
  denied: {
    badge: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400",
    dot: "bg-red-500",
  },
  cancelled: {
    badge: "bg-muted text-muted-foreground",
    dot: "bg-muted-foreground",
  },
}

function formatDate(dateStr: string) {
  const date = new Date(`${dateStr}T00:00:00`)
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

function formatDateRange(start: string, end: string) {
  const s = new Date(`${start}T00:00:00`)
  const e = new Date(`${end}T00:00:00`)
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()
  if (sameMonth) {
    return `${s.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${e.getDate()}`
  }
  return `${formatDate(start)} - ${formatDate(end)}`
}

function StatusBadge({ status }: { status: string }) {
  const style = statusStyles[status] || statusStyles.cancelled
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${style.badge}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${style.dot}`} />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  )
}

function ProgressBar({ value, max, className }: { value: number; max: number; className?: string }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0
  return (
    <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary">
      <div className={`h-full rounded-full transition-all duration-500 ${className}`} style={{ width: `${pct}%` }} />
    </div>
  )
}

function ManagerCodeCard({ code, secondsRemaining }: { code: string; secondsRemaining: number }) {
  const [remaining, setRemaining] = useState(secondsRemaining)

  useEffect(() => {
    setRemaining(secondsRemaining)
  }, [secondsRemaining])

  useEffect(() => {
    const interval = setInterval(() => {
      setRemaining((prev) => {
        if (prev <= 1) {
          router.reload({ only: ["manager_code"] })
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(interval)
  }, [])

  const hours = Math.floor(remaining / 3600)
  const minutes = Math.floor((remaining % 3600) / 60)
  const seconds = remaining % 60
  const countdown = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}:${String(seconds).padStart(2, "0")}`

  return (
    <Card className="gap-0 py-0">
      <CardContent className="flex items-center gap-4 px-5 py-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
          <IconShieldLock className="h-5 w-5 text-primary" />
        </div>
        <div>
          <p className="text-2xl font-semibold tabular-nums tracking-tight">{code}</p>
          <p className="text-xs text-muted-foreground">Manager code · refreshes in {countdown}</p>
        </div>
      </CardContent>
    </Card>
  )
}

export default function DashboardIndex() {
  const {
    balances,
    recent_requests,
    approved_requests_count,
    pending_requests_count,
    all_pending_requests,
    total_pending_admin,
    total_users,
    manager_code,
    auth,
  } = usePage<DashboardProps>().props

  const isAdmin = auth.is_admin
  const firstName = auth.user.name?.split(" ")[0] || "there"

  return (
    <>
      <Head title="Dashboard - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        {/* Header */}
        <div className="flex items-end justify-between">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Welcome back, {firstName}</h1>
            <p className="mt-1 text-sm text-muted-foreground">Here's an overview of your time off and requests.</p>
          </div>
          <Button size="sm" asChild>
            <a href="/time_off_requests/new">
              <IconPlus className="mr-2 h-4 w-4" />
              New Request
            </a>
          </Button>
        </div>

        {/* Balance Cards */}
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {balances.length > 0 ? (
            balances.map((balance) => {
              const config = balanceConfig[balance.balance_type] || balanceConfig.holiday
              const Icon = config.icon
              const used = balance.total_available - balance.total_remaining
              return (
                <Card key={balance.balance_type} className="gap-4 py-5">
                  <CardContent className="px-5">
                    <div className="flex items-center justify-between">
                      <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${config.bg}`}>
                        <Icon className={`h-4.5 w-4.5 ${config.color}`} />
                      </div>
                      <span className="text-xs font-medium text-muted-foreground">
                        {balance.total_remaining}/{balance.total_available}
                      </span>
                    </div>
                    <div className="mt-3">
                      <p className="text-sm font-medium text-muted-foreground">{config.label}</p>
                      <p className="mt-0.5 text-2xl font-semibold tabular-nums tracking-tight">
                        {balance.total_remaining}
                        <span className="ml-1 text-sm font-normal text-muted-foreground">days left</span>
                      </p>
                    </div>
                    <div className="mt-3">
                      <ProgressBar value={used} max={balance.total_available} className={config.track} />
                    </div>
                    {balance.entries.length > 1 && (
                      <div className="mt-3 space-y-1">
                        {balance.entries.map((entry) => (
                          <div
                            key={entry.year}
                            className="flex items-center justify-between text-xs text-muted-foreground"
                          >
                            <span>{entry.year}</span>
                            <span className="tabular-nums">
                              {entry.remaining_days} of {entry.days_available}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })
          ) : (
            <Card className="col-span-full gap-4 py-5">
              <CardContent className="flex items-center gap-4 px-5">
                <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-muted">
                  <IconBeach className="h-4.5 w-4.5 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-medium">No balances configured</p>
                  <p className="text-xs text-muted-foreground">Contact your admin to set up time off balances.</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Stats Row */}
        <div className={`grid gap-4 ${isAdmin ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2"}`}>
          <Card className="gap-0 py-0">
            <CardContent className="flex items-center gap-4 px-5 py-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 dark:bg-emerald-500/20">
                <IconCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums tracking-tight">{approved_requests_count}</p>
                <p className="text-xs text-muted-foreground">Approved requests</p>
              </div>
            </CardContent>
          </Card>
          <Card className="gap-0 py-0">
            <CardContent className="flex items-center gap-4 px-5 py-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-500/15 dark:bg-amber-500/20">
                <IconClock className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <p className="text-2xl font-semibold tabular-nums tracking-tight">{pending_requests_count}</p>
                <p className="text-xs text-muted-foreground">Pending requests</p>
              </div>
            </CardContent>
          </Card>
          {isAdmin && (
            <>
              <Card className="gap-0 py-0">
                <CardContent className="flex items-center gap-4 px-5 py-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-500/15 dark:bg-violet-500/20">
                    <IconAlertTriangle className="h-5 w-5 text-violet-600 dark:text-violet-400" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold tabular-nums tracking-tight">{total_pending_admin}</p>
                    <p className="text-xs text-muted-foreground">Needs review</p>
                  </div>
                </CardContent>
              </Card>
              <Card className="gap-0 py-0">
                <CardContent className="flex items-center gap-4 px-5 py-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                    <IconUsers className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-semibold tabular-nums tracking-tight">{total_users}</p>
                    <p className="text-xs text-muted-foreground">Active users</p>
                  </div>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {isAdmin && manager_code && (
          <ManagerCodeCard code={manager_code.code} secondsRemaining={manager_code.seconds_remaining} />
        )}

        {/* Tables */}
        <div className={`grid gap-6 ${isAdmin ? "lg:grid-cols-2" : ""}`}>
          {/* Recent Requests */}
          <Card className="gap-0">
            <CardHeader className="border-b pb-4">
              <div>
                <CardTitle className="text-base">Recent Requests</CardTitle>
                <CardDescription className="mt-1">Your latest time off requests</CardDescription>
              </div>
              <CardAction>
                <Button variant="ghost" size="sm" className="text-xs" asChild>
                  <a href="/time_off_requests">
                    View all
                    <IconArrowRight className="ml-1 h-3.5 w-3.5" />
                  </a>
                </Button>
              </CardAction>
            </CardHeader>
            <CardContent className="p-0">
              {recent_requests.length > 0 ? (
                <div className="divide-y">
                  {recent_requests.map((request) => {
                    const typeConfig = balanceConfig[request.request_type] || balanceConfig.holiday
                    const TypeIcon = typeConfig.icon
                    return (
                      <a
                        key={request.id}
                        href={`/time_off_requests/${request.id}`}
                        className="flex items-center gap-4 px-6 py-3.5 transition-colors hover:bg-muted/50"
                      >
                        <div
                          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${typeConfig.bg}`}
                        >
                          <TypeIcon className={`h-4 w-4 ${typeConfig.color}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium capitalize">{request.request_type}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDateRange(request.start_date, request.end_date)}
                            <span className="mx-1.5">·</span>
                            {request.days_requested} {request.days_requested === 1 ? "day" : "days"}
                          </p>
                        </div>
                        <StatusBadge status={request.status} />
                      </a>
                    )
                  })}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted">
                    <IconBeach className="h-5 w-5 text-muted-foreground" />
                  </div>
                  <p className="mt-3 text-sm font-medium">No requests yet</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Create your first time off request to get started.
                  </p>
                  <Button size="sm" className="mt-4" asChild>
                    <a href="/time_off_requests/new">
                      <IconPlus className="mr-2 h-3.5 w-3.5" />
                      New Request
                    </a>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Admin: Needs Review */}
          {isAdmin && all_pending_requests && (
            <Card className="gap-0">
              <CardHeader className="border-b pb-4">
                <div>
                  <CardTitle className="text-base">Needs Review</CardTitle>
                  <CardDescription className="mt-1">Pending requests from the team</CardDescription>
                </div>
                <CardAction>
                  <Button variant="ghost" size="sm" className="text-xs" asChild>
                    <a href="/admin/time_off_requests">
                      View all
                      <IconArrowRight className="ml-1 h-3.5 w-3.5" />
                    </a>
                  </Button>
                </CardAction>
              </CardHeader>
              <CardContent className="p-0">
                {all_pending_requests.length > 0 ? (
                  <div className="divide-y">
                    {all_pending_requests.map((request) => (
                      <a
                        key={request.id}
                        href={`/admin/time_off_requests/${request.id}`}
                        className="flex items-center gap-4 px-6 py-3.5 transition-colors hover:bg-muted/50"
                      >
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Avatar size="sm" className="rounded-full">
                                {request.user_avatar_url ? (
                                  <AvatarImage src={request.user_avatar_url} alt={request.user_name || ""} />
                                ) : null}
                                <AvatarFallback className="rounded-full text-[10px]">
                                  {(request.user_name || "?").charAt(0)}
                                </AvatarFallback>
                              </Avatar>
                            </TooltipTrigger>
                            <TooltipContent>{request.user_name}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">{request.user_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDateRange(request.start_date, request.end_date)}
                            <span className="mx-1.5">·</span>
                            <span className="capitalize">{request.request_type}</span>
                          </p>
                        </div>
                        <Badge variant="secondary" className="tabular-nums">
                          {request.days_requested} {request.days_requested === 1 ? "day" : "days"}
                        </Badge>
                      </a>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500/15 dark:bg-emerald-500/20">
                      <IconCheck className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <p className="mt-3 text-sm font-medium">All caught up</p>
                    <p className="mt-1 text-xs text-muted-foreground">No pending requests to review.</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </>
  )
}

DashboardIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
